import api from './api';
import axios from 'axios';
import { logger } from '../utils/logger';

export interface UploadResponse {
  url: string;
  key: string;
  fileName: string;
}

export const uploadService = {
  async uploadAudio(file: File): Promise<UploadResponse> {
    try {
      // Step 1: Get presigned URL from backend
      logger.debug('Requesting presigned URL', {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size
      });

      const presignedResponse = await api.post<{
        uploadUrl: string;
        key: string;
        expiresIn: number;
        bucket: string;
      }>('/upload', {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size
      });

      // Do not log the full presigned response — the uploadUrl is a signed,
      // sensitive S3 URL.
      logger.debug('Received presigned URL', { key: presignedResponse.key });

      // Step 2: Upload file directly to S3 using presigned URL
      logger.debug('Uploading to S3');
      await axios.put(presignedResponse.uploadUrl, file, {
        headers: {
          'Content-Type': file.type
        }
      });

      logger.debug('S3 upload successful');

      // Step 3: Return the CloudFront URL (not direct S3 URL)
      const audioCloudFrontDomain = process.env.REACT_APP_AUDIO_CLOUDFRONT_DOMAIN || 'd2m3bicf9rd0ra.cloudfront.net';
      const audioUrl = `https://${audioCloudFrontDomain}/${presignedResponse.key}`;

      return {
        url: audioUrl,
        key: presignedResponse.key,
        fileName: file.name
      };
    } catch (error: any) {
      // Log only safe metadata — never the full response body.
      logger.error('Upload error', error, {
        status: error.response?.status
      });
      throw new Error(error.response?.data?.message || error.message || 'Upload failed');
    }
  },

  async uploadImage(file: File): Promise<UploadResponse> {
    try {
      // Step 1: Get presigned URL from backend
      logger.debug('Requesting presigned URL for image', {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size
      });

      const presignedResponse = await api.post<{
        uploadUrl: string;
        key: string;
        expiresIn: number;
        bucket: string;
      }>('/upload', {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size
      });

      // Do not log the full presigned response — the uploadUrl is a signed,
      // sensitive S3 URL.
      logger.debug('Received presigned URL for image', { key: presignedResponse.key });

      // Step 2: Upload file directly to S3 using presigned URL
      logger.debug('Uploading image to S3');
      await axios.put(presignedResponse.uploadUrl, file, {
        headers: {
          'Content-Type': file.type
        }
      });

      logger.debug('Image upload successful');

      // Step 3: Return the CloudFront URL
      const audioCloudFrontDomain = process.env.REACT_APP_AUDIO_CLOUDFRONT_DOMAIN || 'd2m3bicf9rd0ra.cloudfront.net';
      const imageUrl = `https://${audioCloudFrontDomain}/${presignedResponse.key}`;

      return {
        url: imageUrl,
        key: presignedResponse.key,
        fileName: file.name
      };
    } catch (error: any) {
      // Log only safe metadata — never the full response body.
      logger.error('Image upload error', error, {
        status: error.response?.status
      });
      throw new Error(error.response?.data?.message || error.message || 'Image upload failed');
    }
  },

  async getPresignedUrl(fileName: string, contentType: string): Promise<{ url: string; key: string }> {
    return api.post<{ url: string; key: string }>('/upload', {
      fileName,
      contentType
    });
  }
};

export default uploadService;
